import type { DialogModalProps } from 'tradera/components/dialog/dialog-modal';
import DialogModal from 'tradera/components/dialog/dialog-modal';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import { BottomSheet } from 'tradera/components/bottom-sheet/bottom-sheet';
import type { ScrollableModalContentProps } from './scrollable-modal-content';
import { ScrollableModalContent } from './scrollable-modal-content';
import { classNames } from 'tradera/utils/class-names';

export type ScrollableModalProps = Pick<
    ScrollableModalContentProps,
    'children' | 'footer' | 'onClose' | 'title' | 'header' | 'forceZIndex'
> &
    Pick<
        ScrollableModalContentProps,
        'padding' | 'paddingX' | 'paddingTop' | 'paddingBottom'
    > &
    Omit<DialogModalProps, 'onDismiss'> & {
        /**
         * For example "40rem" or "60vw". It will be used like this: `min(${maxWidth}, 100%) for mobile and `min(${maxWidth}, 80vw)` for desktop
         */
        maxWidth?: string;
        /**
         * For example "80vh". It will be used like this: `min(${maxHeight}, 100%)` for mobile and `min(${maxHeight}, 80vh)` for desktop
         */
        maxHeight?: string;
        /**
         * If true, the modal will be rendered as a bottom sheet on mobile
         */
        mobileBottomSheet?: boolean;
        /**
         * If true, the bottom sheet will be rendered as full height
         */
        bottomSheetFullHeight?: boolean;
        show: boolean;
        onTop?: boolean;
        className?: string;
    };

/**
 *
 * @example
 * const [show, setShow] = useState(false);
 *
 * const handleOnClose = () => {
 *    setShow(false);
 * };
 *
 * <ScrollableModal
 *     maxWidth={'45rem'}
 *     maxHeight={'60rem'}
 *     onClose={handleOnClose}
 *     mobileBottomSheet
 *     show={show}
 *     title="This title will be used as content in a HeaderWithCloseButton"
 *     footer={<Button
             theme="primary"
             onClick={handleOnClose}>
             Ok, got it
         </Button>}>
 *     This is the content of the modal!
 * </ScrollableModal>
 */
export const ScrollableModal = ({
    header,
    children,
    footer,
    onClose,
    title,
    maxWidth = '40rem',
    maxHeight = '80vh',
    mobileBottomSheet,
    onTop,
    bottomSheetFullHeight = false,
    forceZIndex,
    show,
    padding = true,
    paddingX,
    paddingTop,
    paddingBottom,
    className,
    ...dialogModalProps
}: ScrollableModalProps) => {
    const isMobile = useIsMobile();

    const content = (
        <ScrollableModalContent
            header={header}
            title={title}
            footer={footer}
            onClose={onClose}
            forceZIndex={forceZIndex}
            // To solve that we cannot send both padding and paddingBottom, paddingTop, paddingX at the same time
            {...(padding !== undefined
                ? { padding }
                : { paddingBottom, paddingTop, paddingX })}>
            {children}
        </ScrollableModalContent>
    );

    if (mobileBottomSheet && isMobile) {
        return (
            <BottomSheet
                onDismiss={onClose}
                onTop={onTop}
                show={show}
                maxHeight={bottomSheetFullHeight ? undefined : maxHeight}
                className={classNames(
                    bottomSheetFullHeight && 'h-100',
                    className,
                )}>
                {content}
            </BottomSheet>
        );
    }

    if (!show) return null;

    return (
        <DialogModal
            className={classNames('w-100', className)}
            mobileFullscreen={false}
            onDismiss={onClose}
            onTop={onTop}
            style={{
                maxWidth: isMobile
                    ? `min(${maxWidth}, 100%)`
                    : `min(${maxWidth}, 80vw)`,
                maxHeight: isMobile
                    ? `min(${maxHeight}, 100%)`
                    : `min(${maxHeight}, 80vh)`,
                flexBasis: 0,
            }}
            {...dialogModalProps}>
            {content}
        </DialogModal>
    );
};
